import useSheetHistory from '@/common/hooks/useSheetHistory';
import { getVardastWorkflowEvents } from '@/modules/hami/apis/parseVardastWorkflowMessages';
import { ChatAssistantEventBubbles } from '@/modules/hami/components/chatAssistantEventBubbles';
import { VARDAST_NAME } from '@/modules/hami/components/chatAssistantTypography';
import { VardastWorkflowProvider, useVardastWorkflow } from '@/modules/hami/context/vardastWorkflowContext';
import { useHamiHostStore } from '@/modules/hami/hostBridge';
import { useHasVardastChatWidget } from '@/modules/hami/hooks/useHasVardastChatWidget';
import { trackVardastDrawerOpen, VardastDrawerOpenSource } from '@/modules/hami/utils/trackVardastDrawerOpen';
import { DsDrawer } from '@/modules/doctorHome/components/DsDrawer';
import { useDoctorDeviceCached } from '@/common/hooks/useDoctorDeviceCached';
import { hasDoctorIdentity } from '@/common/utils/doctorIdentity';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { HAMDAST_CLOSE_VARDAST_EVENT } from '@/modules/hamdast/components/close';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

const EDGE_LANE_CLASS = 'absolute top-[60px] bottom-[200px]';

interface ChatAssistantPanelContext {
  isOpen: boolean;
  hasChatWidget: boolean;
  isWidgetsLoading: boolean;
}

interface ChatAssistantDrawerProps {
  isActive: boolean;
  chatId: string | null;
  children: ReactNode;
  panelContent?: ReactNode | ((ctx: ChatAssistantPanelContext) => ReactNode);
  /** Sokan preview only: the sheet starts open (the real page opens it from the header). */
  defaultOpen?: boolean;
}

interface ChatAssistantDrawerViewProps extends ChatAssistantDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  shouldShowVardastUI: boolean;
  hasChatWidget: boolean;
  isWidgetsLoading: boolean;
}

/**
 * وردست به‌صورت باتم‌شیت معمول سایت (`DsDrawer`) باز می‌شود — از دکمه‌ی سربرگ حامی یا حباب رویدادها.
 * زبانه‌ی کناری و کشیدن از لبه‌ی راست حذف شده‌اند.
 */
const ChatAssistantDrawerView = ({
  isActive,
  chatId,
  children,
  panelContent,
  isOpen,
  setIsOpen,
  shouldShowVardastUI,
  hasChatWidget,
  isWidgetsLoading,
}: ChatAssistantDrawerViewProps) => {
  const { messages } = useVardastWorkflow();
  const workflowEvents = getVardastWorkflowEvents(messages);
  const userId = useUserInfoStore(state => state.info?.id);
  const shouldShowDrawerUI = shouldShowVardastUI && hasChatWidget && !isWidgetsLoading;
  const chatLayerRef = useRef<HTMLDivElement>(null);
  const openSourceRef = useRef<VardastDrawerOpenSource | undefined>();
  const wasOpenRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const vardastAvailable = isActive && shouldShowDrawerUI;

  const open = useCallback(
    (source: VardastDrawerOpenSource) => {
      if (isOpenRef.current) return;
      openSourceRef.current = source;
      setIsOpen(true);
    },
    [setIsOpen],
  );
  const close = useCallback(() => setIsOpen(false), [setIsOpen]);

  useSheetHistory(isOpen, close, 'vardast');

  // Legacy iframe only: the standalone Hami site still talks to the site through postMessage.
  const postToHami = useCallback((message: Record<string, unknown>) => {
    const frame = chatLayerRef.current?.querySelector('iframe') as HTMLIFrameElement | null;
    frame?.contentWindow?.postMessage(message, '*');
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const frame = chatLayerRef.current?.querySelector('iframe') as HTMLIFrameElement | null;
      if (!frame || event.source !== frame.contentWindow) return;
      const type = event.data?.type;
      if (type === 'hami:vardast-query') {
        postToHami({ type: 'hami:vardast-available', available: vardastAvailable, label: VARDAST_NAME });
      } else if (type === 'hami:vardast-open' && vardastAvailable) {
        open('header');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postToHami, vardastAvailable, open]);

  useEffect(() => {
    postToHami({ type: 'hami:vardast-available', available: vardastAvailable, label: VARDAST_NAME });
  }, [postToHami, vardastAvailable]);

  // The embedded messenger reads this (its header shows the assistant button).
  const setHostVardast = useHamiHostStore(state => state.setVardast);
  useEffect(() => {
    setHostVardast({ available: vardastAvailable, label: VARDAST_NAME, open: vardastAvailable ? () => open('header') : null });
    return () => setHostVardast({ available: false, label: '', open: null });
  }, [vardastAvailable, open, setHostVardast]);

  useEffect(() => {
    if (!isActive) setIsOpen(false);
  }, [isActive]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current && chatId) {
      trackVardastDrawerOpen(chatId, { source: openSourceRef.current, userId });
      openSourceRef.current = undefined;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, chatId, userId]);

  const showBubbles = vardastAvailable && !isOpen && workflowEvents.length > 0;

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div ref={chatLayerRef} className="h-full w-full">
        {children}
      </div>

      {showBubbles && (
        <div className={`${EDGE_LANE_CLASS} right-0 z-[100]`} dir="ltr">
          <ChatAssistantEventBubbles
            events={workflowEvents}
            hidden={false}
            onBubbleClick={event => {
              event.stopPropagation();
              open('trigger');
            }}
          />
        </div>
      )}

      <DsDrawer open={isActive && isOpen} onOpenChange={next => !next && close()} title={VARDAST_NAME} fullHeight className="!p-0">
        <div className="flex h-full min-h-0 flex-1 flex-col">
          {typeof panelContent === 'function' ? panelContent({ isOpen, hasChatWidget, isWidgetsLoading }) : panelContent}
        </div>
      </DsDrawer>
    </div>
  );
};

export const ChatAssistantDrawer = (props: ChatAssistantDrawerProps) => {
  const [isOpen, setIsOpen] = useState(props.defaultOpen ?? false);
  const userInfo = useUserInfoStore(state => state.info);

  useEffect(() => {
    const handleHamdastCloseVardast = () => setIsOpen(false);

    window.addEventListener(HAMDAST_CLOSE_VARDAST_EVENT, handleHamdastCloseVardast);

    return () => {
      window.removeEventListener(HAMDAST_CLOSE_VARDAST_EVENT, handleHamdastCloseVardast);
    };
  }, []);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const doctorProfilePending = useUserInfoStore(state => state.doctorProfilePending);
  const isDoctor = hasDoctorIdentity(userInfo, useDoctorDeviceCached());
  const shouldShowVardastUI = isDoctor || doctorProfilePending;
  const shouldFetchWidgets = isLogin && !!userInfo?.id && shouldShowVardastUI;
  const { hasChatWidget, isLoading: isWidgetsLoading } = useHasVardastChatWidget(userInfo?.id, shouldFetchWidgets);
  const vardastEnabled = hasChatWidget && !isWidgetsLoading;

  return (
    <VardastWorkflowProvider chatId={props.chatId} isOpen={isOpen} enabled={vardastEnabled}>
      <ChatAssistantDrawerView
        {...props}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        shouldShowVardastUI={shouldShowVardastUI}
        hasChatWidget={hasChatWidget}
        isWidgetsLoading={isWidgetsLoading}
      />
    </VardastWorkflowProvider>
  );
};

export default ChatAssistantDrawer;
